import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { NetFacadeService } from '../../services/netfacade.service';
import { NavigationService, NavLocation, NavType } from '../../services/navigation.service';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { AgentDto } from 'UvExplorerServer.Dtos.TS/Agents';
import { ScheduledDiscovery } from 'DiscoveryEngine.TS/Discovery';
import { DateService, MessageService } from 'base';


@Component({
    selector: 'discovery-list',
	templateUrl: './discoverylist.component.html',
	styleUrls: ['./discoverylist.component.scss']
})

export class DiscoveryListComponent implements OnInit, OnDestroy {

	@Input()
	public agent: AgentDto;
	public get hasAgent(): boolean {
		return !!this.agent;
	}

	public discoveries: ScheduledDiscovery[];

	public displayNewDiscoveryDialog: boolean;
	public displayDeleteDiscoveryConfirm: boolean;

	private netViewTreeLoadedSub: Subscription;
	private netViewTreeChangedSub: Subscription;
	private netViewTreeUnloadedSub: Subscription;
	private sharesLoadedSub: Subscription;
	private sharesChangedSub: Subscription;
	private sharesUnloadedSub: Subscription;

	constructor(
		private netFacadeService: NetFacadeService,
		private navService: NavigationService,
		private dateService: DateService,
		private messageService: MessageService,) {

		this.agent = null;
		this.discoveries = [];
		this.displayNewDiscoveryDialog = false;
		this.displayDeleteDiscoveryConfirm = false;

		this.netViewTreeLoadedSub = this.netFacadeService.onNetViewTreeLoaded.subscribe(() => {
			this.rebuildDiscoveryList();
		});
		this.netViewTreeChangedSub = this.netFacadeService.onNetViewTreeChanged.subscribe(() => {
			this.rebuildDiscoveryList();
		});
		this.netViewTreeUnloadedSub = this.netFacadeService.onNetViewTreeUnloaded.subscribe(() => {
			this.rebuildDiscoveryList();
		});
		this.sharesLoadedSub = this.netFacadeService.onSharesLoaded.subscribe(() => {
			this.rebuildDiscoveryList();
		});
		this.sharesChangedSub = this.netFacadeService.onSharesChanged.subscribe(() => {
			this.rebuildDiscoveryList();
		});
		this.sharesUnloadedSub = this.netFacadeService.onSharesUnloaded.subscribe(() => {
			this.rebuildDiscoveryList();
		});
    }

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	public ngOnInit() {
		return;
	}

	public ngOnDestroy(): void {
		if (this.netViewTreeLoadedSub) {
			this.netViewTreeLoadedSub.unsubscribe();
			this.netViewTreeLoadedSub = undefined;
		}
		if (this.netViewTreeChangedSub) {
			this.netViewTreeChangedSub.unsubscribe();
			this.netViewTreeChangedSub = undefined;
		}
		if (this.netViewTreeUnloadedSub) {
			this.netViewTreeUnloadedSub.unsubscribe();
			this.netViewTreeUnloadedSub = undefined;
		}
		if (this.sharesLoadedSub) {
			this.sharesLoadedSub.unsubscribe();
			this.sharesLoadedSub = undefined;
		}
		if (this.sharesChangedSub) {
			this.sharesChangedSub.unsubscribe();
			this.sharesChangedSub = undefined;
		}
		if (this.sharesUnloadedSub) {
			this.sharesUnloadedSub.unsubscribe();
			this.sharesUnloadedSub = undefined;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		let change: SimpleChange = null;

		change = changes["agent"];
		if (change) {
			this.rebuildDiscoveryList();
		}
	}

	public onDiscoveryClick(schedDisc: ScheduledDiscovery): void {
		let newNavLoc = new NavLocation(NavType.Discovery);
		newNavLoc.netViewGuid = this.agent.ScopeGuid;
		newNavLoc.agentGuid = this.agent.Guid;
		newNavLoc.schedDiscGuid = schedDisc.GUID;
		this.navService.selectLocation(newNavLoc);
	}

	private discoveryToDeleteGuid: string = "";

	public tryDeleteDiscovery(event: any, discGuid: string): void {
		event.stopPropagation();
		this.discoveryToDeleteGuid = discGuid;
		this.displayDeleteDiscoveryConfirm = true;
	}

	public cancelDeleteDiscovery(): void {
		this.discoveryToDeleteGuid = "";
		this.displayDeleteDiscoveryConfirm = false;
	}

	public deleteDiscovery(): void {
		this.netFacadeService.deleteScheduledDiscovery(this.discoveryToDeleteGuid, this.agent);
		this.discoveryToDeleteGuid = "";
		this.displayDeleteDiscoveryConfirm = false;
	}

	private rebuildDiscoveryList(): void {
		this.discoveries = [];
		this.agent.ScheduledDiscoveries.ForEach((schedDisc: ScheduledDiscovery) => {
			this.discoveries.push(schedDisc);
		}, this);
		this.discoveries.sort((a, b) => a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1);
	}

	public onNewDiscoveryDialogClose(): void {
		setTimeout(() => {
			this.displayNewDiscoveryDialog = false;
		}, 0);
	}

	protected toggleDiscoveryEnableState(schedDisc: ScheduledDiscovery): void {

		schedDisc.Enabled = !schedDisc.Enabled;

		// Make local copy of the agent reference because the agent loaded in this component might
		// change before the asynchronous save operation completes.
		let agent: AgentDto = this.agent;
		this.netFacadeService.updateScheduledDiscovery(schedDisc, agent).subscribe(
			(result: ScheduledDiscovery) => {
				this.messageService.displayInfoMessage(`Discovery successfully ${schedDisc.Enabled ? 'enabled' : 'disabled'}`);
			},
			(error: any) => {
				schedDisc.Enabled = !schedDisc.Enabled;
				this.messageService.displayErrorMessage("Could not update discovery");
			});
	}

	public runAsap(schedDisc: ScheduledDiscovery): void {
		if (!!schedDisc && !!this.agent) {
			this.netFacadeService.runDiscoveryAsap(this.agent, schedDisc.GUID).subscribe(
				(result: any) => {
					this.messageService.displayInfoMessage("Discovery will run ASAP");
				},
				(error: any) => {
					this.messageService.displayErrorMessage("Operation Failed");
				});
		}
	}

}
